"use client";

import { useEffect, useState } from "react";
import type { UserTagSuggestion } from "@/components/posts/user-tag-picker";
import {
  type Participant,
  suggestionToParticipant,
  TeamCard,
} from "./base-match-template";
import { ActivityDisplayPreview } from "./previews";
import { CricketScoreEditor } from "./score-editors/cricket-score-editor";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export type CricketInnings = {
  runs: number;
  wickets: number;
  overs?: number;
};

export type CricketTemplateData = {
  yourInnings: CricketInnings;
  opponentInnings: CricketInnings;
  result: "won-by-runs" | "won-by-wickets" | "loss" | "draw" | "tie" | null;
  /** Who batted second (used to determine "by runs" vs "by wickets"). */
  chasingTeam?: "your-team" | "opponent-team" | null;
  positions: Record<string, "your-team" | "opponent-team">;
  hasScore?: boolean;
};

export type CricketParticipant = Participant;

// ──────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type CricketTemplateProps = {
  currentUser: CricketParticipant;
  onChange: (
    data: CricketTemplateData,
    participants: CricketParticipant[],
  ) => void;
  initialData?: Partial<CricketTemplateData>;
};

export function CricketTemplate({
  currentUser,
  onChange,
  initialData,
}: CricketTemplateProps) {
  const [participants, setParticipants] = useState<CricketParticipant[]>([
    currentUser,
  ]);
  const [teams, setTeams] = useState<
    Record<string, "your-team" | "opponent-team">
  >(() => ({
    [currentUser.id]: "your-team",
    ...(initialData?.positions ?? {}),
  }));
  type CricketScorePart = Pick<
    CricketTemplateData,
    "yourInnings" | "opponentInnings" | "result" | "chasingTeam"
  >;
  const [scorePart, setScorePart] = useState<CricketScorePart>(() => ({
    yourInnings: initialData?.yourInnings ?? { runs: 0, wickets: 0 },
    opponentInnings: initialData?.opponentInnings ?? { runs: 0, wickets: 0 },
    result: initialData?.result ?? null,
    chasingTeam: initialData?.chasingTeam ?? null,
  }));

  // Flexible team sizes (allows subs and casual games)
  const maxPerTeam = 25;

  const yourTeam = participants.filter((p) => teams[p.id] === "your-team");
  const opponentTeam = participants.filter(
    (p) => teams[p.id] === "opponent-team",
  );
  const selectedUserIds = new Set(participants.map((p) => p.id));

  useEffect(() => {
    const data: CricketTemplateData = {
      yourInnings: scorePart.yourInnings,
      opponentInnings: scorePart.opponentInnings,
      result: scorePart.result,
      chasingTeam: scorePart.chasingTeam,
      positions: teams,
      hasScore: true,
    };
    onChange(data, participants);
  }, [scorePart, teams, participants, onChange]);

  const handleAddParticipant = (
    suggestion: UserTagSuggestion,
    team: "your-team" | "opponent-team",
  ) => {
    const teamCount =
      team === "your-team" ? yourTeam.length : opponentTeam.length;
    if (teamCount >= maxPerTeam) return;

    const newParticipant = suggestionToParticipant(suggestion);
    setParticipants((prev) => [...prev, newParticipant]);
    setTeams((prev) => ({ ...prev, [suggestion.id]: team }));
  };

  const handleRemoveParticipant = (userId: string) => {
    if (userId === currentUser.id) return;
    setParticipants((prev) => prev.filter((p) => p.id !== userId));
    setTeams((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const yourWon = scorePart.yourInnings.runs > scorePart.opponentInnings.runs;
  const opponentWon =
    scorePart.opponentInnings.runs > scorePart.yourInnings.runs;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3">
        <TeamCard
          label="Team 1"
          isYourTeam
          players={yourTeam}
          maxPlayers={maxPerTeam}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "your-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={yourWon}
        />
        <TeamCard
          label="Team 2"
          isYourTeam={false}
          players={opponentTeam}
          maxPlayers={maxPerTeam}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "opponent-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={opponentWon}
        />
      </div>

      {!opponentTeam.length && (
        <div className="rounded-lg border border-warning/50 bg-warning/10 px-3 py-2 text-sm text-warning">
          <span>Add at least one player to Team 2</span>
        </div>
      )}

      <CricketScoreEditor
        initialData={initialData as unknown as Record<string, unknown>}
        onChange={(data) => setScorePart(data as unknown as CricketScorePart)}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DISPLAY COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type CricketDisplayProps = {
  data: CricketTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

export function CricketDisplay({ data, participants }: CricketDisplayProps) {
  return (
    <ActivityDisplayPreview
      activityId="cricket"
      data={data as unknown as Record<string, unknown> | null}
      participants={participants}
    />
  );
}
